import { Clipboard } from '@angular/cdk/clipboard';
import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { PendoService } from 'analytics-lib';
import { BullhornAuthService } from 'auth-service-lib';
import { jwtDecode } from 'jwt-decode';
import { MessageNotificationService } from 'message-notification-service-lib';
import { IdentityStorage } from 'storage-service-lib';
import { buildVersion } from '../../../../environments/build.version';
import { environment } from '../../../../environments/environment';
import { ConfigService } from '../../../services/config.service';
import { NotetakerCallsService } from '../../../services/notetaker-calls.service';
import { QuestionPackManagerService } from '../../../services/question-pack-manager.service';
import { QuestionPackService } from '../../../services/questionPack.service';
import { AIApps } from '../../../types/AIApps';
import { IClientConfig } from '../../../types/client-config';
import { IQuestion } from '../../../types/IQuestionManager';
import { IDialogConfirm } from '../../assist-manager-landing/prompt-profile/prompt-profile.component';
import { DialogConfirmComponent } from '../../dialog-confirm/dialog-confirm.component';
import { QuestionModalComponent } from './question-modal/question-modal.component';

@Component({
  selector: 'app-question-manager',
  templateUrl: './question-manager.component.html',
  styleUrl: './question-manager.component.scss',
  standalone: false,
})
export class QuestionManagerComponent {
  get filteredQuestions(): any[] {
    const questions = this.questionsDataSource.filter((q) => {
      const meta = typeof q.meta === 'string' ? JSON.parse(q.meta) : q.meta;

      const isQuestionPack = q.sortKey?.startsWith('QuestionPack-');
      const isSummary = meta?.style === 'SUMMARY';

      return !isQuestionPack && !isSummary;
    });

    if (!this.searchText) {
      return questions;
    }
    const filterValue = this.searchText.trim().toLowerCase();
    return questions.filter((q) => {
      const questionText = (q.meta?.question || '').toLowerCase();
      const field = (q.field || '').toLowerCase();
      const type = (q.type || '').toLowerCase();
      return (
        questionText.includes(filterValue) ||
        field.includes(filterValue) ||
        type.includes(filterValue)
      );
    });
  }
  protected clientConfig!: IClientConfig;
  corpIdDisplay = '';
  expandVersion = false;
  searchText = '';

  questionsDataSource: any[] = [];

  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('clearSearch') clearSearch!: ElementRef;

  protected readonly environment = environment;
  protected readonly appVersion = buildVersion;

  constructor(
    public router: Router,
    private identityStorage: IdentityStorage,
    private dialog: MatDialog,
    private bullhornAuthService: BullhornAuthService,
    private noteTakerCallsService: NotetakerCallsService,
    private questionPackService: QuestionPackService,
    private clientConfigService: ConfigService,
    private messageNotificationService: MessageNotificationService,
    private clipboard: Clipboard,
    private pendoService: PendoService,
    private qpm: QuestionPackManagerService
  ) {}

  ngOnInit(): void {
    this.loadQuestions();
    this.loadPendo();
  }

  private loadPendo() {
    this.pendoService.initPendoScript(environment.ntManagerPendoAPIKey);
  }

  private loadQuestions(): void {
    if (!this.identityStorage.get()) {
      this.bullhornAuthService.getToken().subscribe(() => this.loadQuestions());
      return;
    }

    const { corpId, namespaceId } = this.getCorpDetails();
    this.corpIdDisplay = corpId;
    this.fetchClientConfig(namespaceId);
  }

  private fetchClientConfig(namespaceId: string): void {
    this.clientConfigService
      .getFullConfigForCorpId(
        AIApps.NOTETAKER,
        environment.ntApiGatewayUrl,
        environment.dtApiGatewayUrl,
        namespaceId
      )
      .subscribe({
        next: (config) => this.handleClientConfig(config, namespaceId),
        error: (err) => console.error('Error fetching client config:', err),
      });
  }

  private handleClientConfig(config: any, namespaceId: string): void {
    this.clientConfig = config;

    if (!config.notetakerEnabled) {
      this.messageNotificationService.error('Client is not enabled');
      return;
    }

    this.fetchQuestions(namespaceId);
  }

  private fetchQuestions(namespaceId: string): void {
    this.noteTakerCallsService.getQuestionsByCorp(namespaceId).subscribe({
      next: (questions) => this.processQuestions(questions, namespaceId),
      error: (err) => console.error('Error fetching questions:', err),
    });
  }

  private processQuestions(questions: any[], namespaceId: string) {
    const pack = questions.find((q) => q.sortKey.startsWith('QuestionPack-'));
    if (!pack) {
      this.qpm
        .ensurePackWithDefaultQuestions(
          this.clientConfig,
          this.corpIdDisplay,
          ['Candidate', 'ClientContact']
        )
        .subscribe(() => this.fetchQuestions(namespaceId));
    } else {
      this.loadQuestionsDataSource(questions);
    }
  }

  private loadQuestionsDataSource(questions: any[]): void {
    this.questionsDataSource = questions.map((q) => ({
      ...q,
      field: q.meta?.destination,
      type: q.entityType,
    }));
  }

  private getCorpDetails(): { corpId: string; namespaceId: string } {
    const storedJson = this.identityStorage.get()!;
    const jwt = jwtDecode<any>(storedJson.token);
    const corpId = jwt.corporationId as string;
    const namespaceId = `bullhorn-${corpId}`;
    return { corpId, namespaceId };
  }

  private getCommonGuidFromQuestionPack(): string {
    const questionPack = this.questionsDataSource.find(
      (q) => q.sortKey && q.sortKey.startsWith('QuestionPack-')
    );
    if (questionPack) {
      return questionPack.sortKey.substring('QuestionPack-'.length);
    }
    // todo return null, string
    return 'default-common-guid';
  }

  showModal(
    question: IQuestion = {
      corpId: this.clientConfig?.corpId ?? '',
      entityType: '',
      sortKey: '',
      // todo
      namespace: 'bullhorn',
      corporationId: this.corpIdDisplay,
      meta: {
        question: '',
        type: 'String',
        destination: '',
        additionalRestrictions: '',
        style: 'INFORMATION',
      },
    }
  ): void {
    const commonGuid = this.getCommonGuidFromQuestionPack();
    const questionDialog = this.dialog.open(QuestionModalComponent, {
      maxHeight: '93vh',
      minWidth: '56vw',
      autoFocus: true,
      data: {
        question: question,
        commonGuid: commonGuid,
      },
      disableClose: true,
    });

    questionDialog.backdropClick().subscribe(() => questionDialog.close());

    questionDialog.afterClosed().subscribe((result) => {
      if (!result) return;

      const updated = result.question;
      const index = this.questionsDataSource.findIndex((q) => q.sortKey === updated.sortKey);

      this.noteTakerCallsService.saveQuestion(updated).subscribe({
        next: () => {
          updated.meta = typeof updated.meta === 'string' ? JSON.parse(updated.meta) : updated.meta;

          const transformed = {
            ...updated,
            field: updated.meta?.destination,
            type: updated.entityType,
          };

          if (index !== -1) {
            this.questionsDataSource[index] = transformed;
          } else {
            this.questionsDataSource.push(transformed);
          }

          this.questionsDataSource = [...this.questionsDataSource];

          this.messageNotificationService.success(
            index !== -1 ? 'Question updated successfully' : 'Question saved successfully'
          );
        },
        error: (err) => console.error('Error saving question:', err),
      });
    });
  }

  getStyle(question: IQuestion): string {
    const meta = typeof question.meta === 'string' ? JSON.parse(question.meta) : question.meta;
    return meta?.style || '';
  }

  delete(question: IQuestion, event: any): void {
    event.stopPropagation();
    const questionMeta =
      typeof question.meta === 'string' ? JSON.parse(question.meta) : question.meta;
    if (questionMeta?.style === 'SUMMARY') {
      this.messageNotificationService.error('Summary questions cannot be deleted.');
      return;
    }
    const dialogDelete: MatDialogRef<DialogConfirmComponent> = this.dialog.open(
      DialogConfirmComponent,
      {
        maxWidth: '85vw',
        height: 'auto',
        autoFocus: true,
        data: {
          title: `Delete`,
          message: `Are you sure you want to delete this question?`,
          positiveText: 'Yes',
          negativeText: 'No',
        } as IDialogConfirm,
      }
    );

    dialogDelete.afterClosed().subscribe((takeAction: boolean) => {
      if (!takeAction) return;
      this.deleteQuestion(question);
    });
  }

  deleteQuestion(question: IQuestion) {
    this.noteTakerCallsService.deleteQuestion(question).subscribe({
      next: () => {
        this.messageNotificationService.success(`Question deleted`);

        const callIndex = this.questionsDataSource.indexOf(question);
        if (callIndex !== -1) {
          this.questionsDataSource.splice(callIndex, 1);
          this.questionsDataSource = [...this.questionsDataSource];
        }
      },
      error: (error: any) => {
        this.messageNotificationService.error(`Error deleting question`, error);
      },
    });
  }

  copyToClipboard(text: string): void {
    this.clipboard.copy(text);
    this.messageNotificationService.success(`Copied "${text}" to clipboard`);
  }

  clearInput(inputElement: HTMLInputElement, focusInput: boolean = true): void {
    this.searchText = '';
    inputElement.value = '';
    if (focusInput) {
      inputElement.focus();
    }
  }

  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    const searchInput = this.searchInput?.nativeElement;
    const clearSearch = this.clearSearch?.nativeElement;

    if (event.code === 'Escape' && this.clearSearch) clearSearch.click();

    if (this.dialog.openDialogs.length === 0 && event.ctrlKey && event.shiftKey) {
      if (event.code === 'KeyF') {
        searchInput.focus();
      }
    }

    if (event.altKey && event.shiftKey) {
      if (event.code === 'KeyV') {
        this.expandVersion = true;
        setTimeout(() => {
          this.expandVersion = false;
        }, 3000);
      }
    }
  }
}
